import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import "@/App.css";
import axios from "axios";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MapPin, Phone, Mail, Instagram, Facebook, Check, Compass, Ruler, PenTool, Hammer, Sparkles, Star, Quote } from "lucide-react";
import { translations, PROJECT_IMAGES, HERO_IMAGE, LOGO_URL, PROJECT_SLUGS } from "@/i18n";
import { CITIES } from "@/data/cities";
import { AuthProvider, useAuth } from "@/auth/AuthContext";
import ProtectedRoute from "@/auth/ProtectedRoute";
import ExitIntent from "@/components/ExitIntent";
import ScrollManager from "@/components/ScrollManager";
import { NetworkBadges } from "@/components/NetworkBadges";

// Code-split secondary routes — the homepage no longer downloads Admin/Blog/
// Process/Glossary/Team JS, slashing main bundle JS by ~261 KB and the long
// task on first paint. Each route loads on-demand on first navigation.
const ProjectDetail = lazy(() => import("@/pages/ProjectDetail"));
const Blog = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.Blog })));
const BlogArticle = lazy(() => import("@/pages/Blog").then((m) => ({ default: m.BlogArticle })));
const LegalMentions = lazy(() => import("@/pages/LegalMentions"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Team = lazy(() => import("@/pages/Team"));
const Glossary = lazy(() => import("@/pages/Glossary"));
const Process = lazy(() => import("@/pages/Process"));
const City = lazy(() => import("@/pages/City"));
const AuthCallback = lazy(() => import("@/auth/AuthCallback"));
const Login = lazy(() => import("@/pages/Login"));
const AdminShell = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminShell })));
const AdminDashboard = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminDashboard })));
const AdminContacts = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminContacts })));
const AdminAppointments = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminAppointments })));
const AdminBlog = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminBlog })));
const AdminContent = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.AdminContent })));

const RouteFallback = () => (
  <div className="min-h-[40vh] flex items-center justify-center" data-testid="route-loading">
    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/40">Chargement…</div>
  </div>
);
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { fr as dfFr, enGB as dfEn } from "date-fns/locale";
import useSeo from "@/hooks/useSeo";
import { SiteContentProvider, useSiteContent } from "@/contexts/SiteContent";
import { mergeTranslationsWithContent, resolveHeroImage } from "@/utils/content";

axios.defaults.withCredentials = true;

import { getApiBase } from "@/utils/apiBase";
const API = getApiBase();

/* ============================================================
   NAV
============================================================ */
const Nav = ({ lang, setLang, t }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "about", label: t.nav.about },
    { id: "services", label: t.nav.services },
    { id: "projects", label: t.nav.projects },
    { id: "testimonials", label: t.nav.testimonials },
    { id: "simulator", label: t.nav.simulator },
    { id: "contact", label: t.nav.contact },
  ];

  const hrefFor = (id) => (onHome ? `#${id}` : `/#${id}`);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled || !onHome ? "nav-glass py-3" : "py-5"}`}
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 md:gap-4 group min-w-0" data-testid="logo-link">
          <img
            src={LOGO_URL}
            alt="Cabinet ECHO — Maîtrise d'œuvre Charente-Maritime"
            width="80"
            height="80"
            className="w-16 h-16 md:w-20 md:h-20 object-contain transition-transform duration-500 group-hover:rotate-[-4deg]"
          />
          <div className="flex flex-col leading-none min-w-0">
            <span className="font-mono text-[10px] md:text-[11px] tracking-[0.38em] text-terracotta uppercase">Cabinet</span>
            <span className="font-serif text-4xl md:text-6xl font-semibold tracking-[-0.02em] text-ink -mt-0.5 leading-none">
              ECHO
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <a
              key={l.id}
              href={hrefFor(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/80 hover:text-terracotta transition-colors"
            >
              {l.label}
            </a>
          ))}
          <Link
            to="/blog"
            data-testid="nav-link-blog"
            className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink/80 hover:text-terracotta transition-colors"
          >
            {t.nav.blog}
          </Link>
        </nav>

        <div className="flex items-center gap-3 md:gap-4 shrink-0">
          <div className="hidden md:flex items-center font-mono text-[11px] tracking-[0.2em] uppercase border border-gridline">
            <button
              onClick={() => setLang("fr")}
              data-testid="lang-fr"
              className={`px-3 py-1.5 transition-colors ${lang === "fr" ? "bg-ink text-limestone" : "text-ink hover:bg-stone"}`}
            >
              FR
            </button>
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-ink text-limestone" : "text-ink hover:bg-stone"}`}
            >
              EN
            </button>
          </div>
          <a href={onHome ? "#contact" : "/#contact"} data-testid="nav-cta" className="btn-primary !hidden lg:!inline-flex">
            {t.nav.cta} <ArrowRight size={14} strokeWidth={1.5} />
          </a>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden flex flex-col gap-1.5 p-2"
            aria-label="menu"
            data-testid="mobile-menu-btn"
          >
            <span className={`w-6 h-px bg-ink transition-transform ${open ? "rotate-45 translate-y-1.5" : ""}`} />
            <span className={`w-6 h-px bg-ink transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`w-6 h-px bg-ink transition-transform ${open ? "-rotate-45 -translate-y-1.5" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden nav-glass mt-3" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.id}
                href={hrefFor(l.id)}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.id}`}
                className="font-mono text-xs uppercase tracking-[0.2em] text-ink"
              >
                {l.label}
              </a>
            ))}
            <Link to="/blog" onClick={() => setOpen(false)} data-testid="mobile-nav-link-blog" className="font-mono text-xs uppercase tracking-[0.2em] text-ink">
              {t.nav.blog}
            </Link>
            <a href={onHome ? "#contact" : "/#contact"} onClick={() => setOpen(false)} data-testid="mobile-nav-cta" className="btn-primary mt-4 self-start">
              {t.nav.cta} <ArrowRight size={14} strokeWidth={1.5} />
            </a>
            {/* Lang switch inside mobile menu */}
            <div className="flex items-center font-mono text-[11px] tracking-[0.2em] uppercase border border-gridline self-start mt-2" data-testid="mobile-lang-switch">
              <button
                onClick={() => { setLang("fr"); setOpen(false); }}
                data-testid="mobile-lang-fr"
                className={`px-3 py-1.5 transition-colors ${lang === "fr" ? "bg-ink text-limestone" : "text-ink"}`}
              >
                FR
              </button>
              <button
                onClick={() => { setLang("en"); setOpen(false); }}
                data-testid="mobile-lang-en"
                className={`px-3 py-1.5 transition-colors ${lang === "en" ? "bg-ink text-limestone" : "text-ink"}`}
              >
                EN
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

/* ============================================================
   HERO
============================================================ */
const heroSrcSet = (src) => {
  if (!src) return null;
  const lower = src.toLowerCase();
  if (lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png")) {
    const base = src.replace(/\.(jpg|jpeg|png)$/i, "");
    // ?v=2 cache-bust forces Cloudflare edge to re-fetch from origin if a stale
    // 404 was previously cached during the deployment window before the
    // auto-conversion middleware had generated the optimised variants.
    return { avif: `${base}.avif?v=2`, webp: `${base}.webp?v=2`, fallback: src };
  }
  return null;
};

const Hero = ({ t, heroImage }) => {
  const sources = heroSrcSet(heroImage);
  return (
  <section id="top" className="relative pt-36 md:pt-44 pb-20 md:pb-32 overflow-hidden">
    <div className="absolute inset-0 blueprint-lines opacity-60 pointer-events-none" />
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 relative">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
        <div className="md:col-span-7 fade-in-up">
          <div className="section-label mb-10">{t.hero.tag}</div>
          <h1 className="font-serif text-[13vw] md:text-[8.5vw] leading-[0.9] text-ink font-light tracking-tight">
            {t.hero.title1} <br />
            {t.hero.title2} <span className="italic font-normal text-forest">{t.hero.title3}</span>
          </h1>
          <p className="font-serif italic text-2xl md:text-3xl text-terracotta mt-6">— {t.hero.italic}</p>
          <p className="font-sans text-base md:text-lg text-ink/75 max-w-xl mt-10 leading-relaxed">{t.hero.intro}</p>

          {/* Trust strip — concrete proof points above the fold */}
          {t.hero.trustStats && (
            <a
              href="#testimonials"
              className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-4 mt-10 border-t border-b border-gridline py-6 max-w-2xl group"
              data-testid="hero-trust-strip"
              aria-label={t.hero.reviewsLabel}
            >
              {t.hero.trustStats.map((stat, i) => (
                <div key={i} className="flex flex-col">
                  <div className="flex items-baseline gap-1">
                    <span className="font-serif text-3xl md:text-4xl text-ink leading-none group-hover:text-forest transition-colors">{stat.value}</span>
                    {stat.unit && (
                      <span className="font-mono text-xs text-ink/50 uppercase tracking-wider">{stat.unit}</span>
                    )}
                  </div>
                  <span className="font-mono text-[10px] tracking-[0.15em] uppercase text-ink/60 mt-2 leading-tight">
                    {stat.label}
                  </span>
                </div>
              ))}
            </a>
          )}

          <div className="flex flex-wrap gap-4 mt-10">
            <a href="#contact" className="btn-primary" data-testid="hero-cta-primary">
              {t.hero.primaryCta} <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>
            <a href="#projects" className="btn-outline" data-testid="hero-cta-secondary">
              {t.hero.secondaryCta}
            </a>
            <a href="#simulator" className="btn-outline" data-testid="hero-cta-simulator">
              {t.hero.simulatorCta}
            </a>
          </div>
        </div>

        <div className="md:col-span-5 relative">
          <div className="relative aspect-[4/5] overflow-hidden">
            {sources ? (
              <picture>
                <source srcSet={sources.avif} type="image/avif" />
                <source srcSet={sources.webp} type="image/webp" />
                <img
                  src={sources.fallback}
                  alt="Maison balnéaire avec extension contemporaine bardage bois — Cabinet ECHO"
                  width="1200"
                  height="1500"
                  className="w-full h-full object-cover hover-desaturate"
                  loading="eager"
                  fetchPriority="high"
                />
              </picture>
            ) : (
              <img
                src={heroImage}
                alt="Maison balnéaire avec extension contemporaine bardage bois — Cabinet ECHO"
                width="1200"
                height="1500"
                className="w-full h-full object-cover hover-desaturate"
                loading="eager"
                fetchPriority="high"
              />
            )}
          </div>
          <div className="mt-4 flex items-center justify-between font-mono text-[11px] tracking-[0.15em] uppercase text-ink/60">
            <span>46°09'N · 1°09'W</span>
            <span>La Rochelle · FR</span>
          </div>
        </div>
      </div>
    </div>
  </section>
  );
};

/* ============================================================
   MARQUEE
============================================================ */
const Marquee = ({ items }) => {
  const full = [...items, ...items, ...items];
  return (
    <section className="border-y border-gridline bg-stone/60 overflow-hidden py-6" data-testid="marquee">
      <div className="flex marquee-track whitespace-nowrap">
        {full.map((it, i) => (
          <div key={i} className="flex items-center gap-10 px-10 font-serif italic text-3xl md:text-5xl text-ink/80">
            <span>{it}</span>
            <span className="text-terracotta font-sans not-italic text-lg">✦</span>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ============================================================
   ABOUT / MANIFESTO
============================================================ */
const About = ({ t }) => {
  const icons = [Compass, Ruler, PenTool, Hammer];
  return (
    <section id="about" className="py-24 md:py-36 relative overflow-hidden">
      {/* Soft colored accents — barely visible on desktop */}
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-terracotta/[0.07] blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute bottom-20 -left-20 w-80 h-80 rounded-full bg-forest/[0.06] blur-3xl" />
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="section-label mb-6">{t.about.label}</div>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] text-ink font-light">
              {t.about.title} <span className="italic text-forest">{t.about.titleItalic}</span>{t.about.titleEnd}
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <p className="font-sans text-lg md:text-xl text-ink/80 leading-relaxed">{t.about.lead}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gridline mt-20 border border-gridline">
          {t.about.pillars.map((p, i) => {
            const Icon = icons[i];
            return (
              <div key={i} className="bg-limestone p-8 md:p-10 hover:bg-stone transition-colors group" data-testid={`pillar-${i}`}>
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 flex items-center justify-center border border-forest/20 group-hover:border-forest group-hover:bg-forest/10 transition-all">
                    <Icon size={22} strokeWidth={1.2} className="text-forest" />
                  </div>
                  <span className="font-mono text-xs tracking-[0.25em] text-terracotta">{p.n}</span>
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-ink mt-10">{p.t}</h3>
                <p className="font-sans text-sm text-ink/70 mt-4 leading-relaxed">{p.d}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   SERVICES
============================================================ */
const Services = ({ t }) => (
  <section id="services" className="py-24 md:py-36 bg-stone/50 border-y border-gridline">
    <div className="max-w-[1500px] mx-auto px-6 md:px-10">
      <div className="section-label mb-6">{t.services.label}</div>
      <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] text-ink font-light max-w-4xl">
        {t.services.title} <span className="italic text-terracotta">{t.services.titleItalic}</span>{t.services.titleEnd}
      </h2>

      <div className="mt-16 divide-y divide-gridline border-y border-gridline">
        {t.services.items.map((s, i) => (
          <div
            key={i}
            className="py-10 md:py-14 grid grid-cols-1 md:grid-cols-12 gap-6 group cursor-pointer transition-colors hover:bg-limestone/60 px-4 -mx-4"
            data-testid={`service-${i}`}
          >
            <div className="md:col-span-1 font-mono text-xs tracking-[0.25em] text-terracotta">0{i + 1}</div>
            <h3 className="md:col-span-4 font-serif text-3xl md:text-5xl text-ink group-hover:text-terracotta transition-colors">
              {s.t}
            </h3>
            <p className="md:col-span-5 font-sans text-base text-ink/75 leading-relaxed">{s.d}</p>
            <div className="md:col-span-2 flex flex-wrap items-start gap-2">
              {s.tags.map((tag, j) => (
                <span key={j} className="font-mono text-[10px] tracking-[0.2em] uppercase text-forest bg-forest/5 border border-forest/20 px-2 py-1 group-hover:bg-forest/10 transition-colors">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ============================================================
   PROJECTS GALLERY
============================================================ */
const Projects = ({ t }) => {
  // Editorial asymmetric layout with FIXED row heights so any aspect ratio fills
  // its cell via object-cover — no more white gaps between cards.
  //   row 1-2: [tall hero 7w x 2h] [5w] [5w]
  //   row 3:   [6w] [6w]
  //   row 4:   [4w] [4w] [4w]
  //   row 5:   [12w full-width — last project / latest entry]
  const layouts = [
    "md:col-span-7 md:row-span-2",
    "md:col-span-5",
    "md:col-span-5",
    "md:col-span-6",
    "md:col-span-6",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-4",
    "md:col-span-12",
  ];

  return (
    <section id="projects" className="py-24 md:py-36">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <div className="section-label mb-6">{t.projects.label}</div>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] text-ink font-light max-w-4xl">
              {t.projects.title} <span className="italic text-forest">{t.projects.titleItalic}</span>
            </h2>
          </div>
          <a href="#contact" className="btn-outline" data-testid="projects-cta">
            {t.projects.cta} <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
          <a href="#simulator" className="btn-outline md:inline-flex hidden" data-testid="projects-cta-simulator">
            {t.hero.simulatorCta}
          </a>
        </div>

        <div
          className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8 md:auto-rows-[20rem]"
          style={{ gridAutoFlow: "dense" }}
        >
          {t.projects.items.map((p, i) => (
            <Link
              key={i}
              to={`/projets/${p.slug || PROJECT_SLUGS[i]}`}
              className={`relative overflow-hidden group block aspect-[4/3] md:aspect-auto ${layouts[i % layouts.length]}`}
              data-testid={`project-${i}`}
            >
              <img
                src={p.cover || PROJECT_IMAGES[i]}
                alt={`${p.t} — ${p.cat} ${p.l ? "à " + p.l : ""}`}
                width="1024"
                height="768"
                className="absolute inset-0 w-full h-full object-cover hover-desaturate"
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent opacity-90 pointer-events-none" />
              <div className="absolute top-4 left-4 right-4 flex items-start justify-between font-mono text-[10px] tracking-[0.25em] uppercase text-limestone">
                <span>[{p.y}]</span>
                <span>[{p.cat}]</span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-limestone">
                <h3 className="font-serif text-2xl md:text-4xl leading-tight group-hover:text-terracotta transition-colors">{p.t}</h3>
                <div className="flex items-center justify-between mt-3">
                  <p className="font-sans text-sm text-limestone/80 max-w-md">{p.d}</p>
                  <span className="font-mono text-[10px] tracking-[0.2em] uppercase whitespace-nowrap ml-4 inline-flex items-center gap-1">
                    ◦ {p.l} <ArrowUpRight size={12} strokeWidth={1.5} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   TESTIMONIALS — Paroles de clients (Google Reviews)
============================================================ */
const Testimonials = ({ t }) => {
  const items = t.testimonials?.items || [];
  if (items.length === 0) return null;

  return (
    <section id="testimonials" className="py-24 md:py-36 bg-stone/50 border-y border-gridline relative overflow-hidden" data-testid="testimonials-section">
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -right-20 w-[480px] h-[480px] rounded-full bg-terracotta/[0.06] blur-3xl" />
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 relative">
        <div className="mb-16 max-w-3xl">
          <div className="section-label mb-6">{t.testimonials.label}</div>
          <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] text-ink font-light">
            {t.testimonials.title} <span className="italic text-forest">{t.testimonials.titleItalic}</span>{t.testimonials.titleEnd}
          </h2>
          <p className="font-sans text-base text-ink/75 mt-8 leading-relaxed">{t.testimonials.intro}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {items.map((r, i) => (
            <article
              key={i}
              className="relative border border-gridline bg-limestone p-8 md:p-10 flex flex-col group hover:border-forest transition-colors"
              data-testid={`testimonial-${i}`}
            >
              <Quote size={32} strokeWidth={1} className="text-forest/30 absolute top-6 right-6 group-hover:text-forest/60 transition-colors" />
              <div role="img" className="flex gap-0.5 text-terracotta mb-5" aria-label={`${r.rating} étoiles`}>
                {Array.from({ length: Number(r.rating) || 5 }).map((_, n) => (
                  <Star key={n} size={14} strokeWidth={1.2} fill="currentColor" />
                ))}
              </div>
              <p className="font-serif text-lg md:text-xl text-ink/90 leading-relaxed italic flex-1">
                « {r.text} »
              </p>
              <div className="mt-8 pt-6 border-t border-gridline">
                <div className="font-sans text-sm font-medium text-ink">{r.name}</div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 mt-1.5">
                  {r.location ? `${r.location} · ` : ""}{t.testimonials.visited} {r.date}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};


/* ============================================================
   COST SIMULATOR
============================================================ */
const Simulator = ({ t }) => {
  const [surface, setSurface] = useState(120);
  const [projectType, setProjectType] = useState("renovation_complete");
  const [finish, setFinish] = useState("premium");
  const [result, setResult] = useState(null);          // set ONLY after lead success
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "", hp_homepage_url: "" });
  const [leadStatus, setLeadStatus] = useState(null);
  const [leadSubmitting, setLeadSubmitting] = useState(false);

  // Reset any previously revealed estimate as soon as the user changes inputs
  useEffect(() => { setResult(null); }, [surface, projectType, finish]);

  const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n);

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadSubmitting(true);
    setLeadStatus(null);
    try {
      const res = await axios.post(`${API}/estimate`, {
        ...leadForm,
        surface_m2: surface,
        project_type: projectType,
        finish_level: finish,
      });
      setResult({
        low_estimate_eur: res.data.low_estimate_eur,
        high_estimate_eur: res.data.high_estimate_eur,
      });
      setLeadStatus("success");
      setLeadForm({ name: "", email: "", phone: "", hp_homepage_url: "" });
      // Auto-close the modal after 1.8s so the reveal is visible behind it
      setTimeout(() => setLeadOpen(false), 1800);
    } catch (err) {
      setLeadStatus(err?.response?.status === 429 ? "rate_limit" : "error");
    } finally {
      setLeadSubmitting(false);
    }
  };

  return (
    <section id="simulator" className="py-24 md:py-36 bg-forest text-limestone relative overflow-hidden">
      <div className="absolute inset-0 opacity-10 pointer-events-none blueprint-grid" />
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="section-label mb-6" style={{ color: "#B04A37" }}>
              <span style={{ background: "#B04A37" }} />{t.simulator.label}
            </div>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] font-light">
              {t.simulator.title} <span className="italic text-terracotta">{t.simulator.titleItalic}</span>
              {t.simulator.titleEnd}
            </h2>
            <p className="font-sans text-base text-limestone/70 mt-8 max-w-md leading-relaxed">{t.simulator.intro}</p>
          </div>

          <div className="md:col-span-7 border border-limestone/20 p-8 md:p-12" data-testid="simulator-panel">
            <div>
              <div className="flex items-baseline justify-between">
                <label htmlFor="sim-surface-range" className="font-mono text-xs tracking-[0.2em] uppercase text-limestone/70">{t.simulator.surface}</label>
                <span className="font-mono text-2xl md:text-4xl text-terracotta" data-testid="simulator-surface-display">
                  {surface} <span className="text-sm text-limestone/60">m²</span>
                </span>
              </div>
              <input id="sim-surface-range" type="range" min="20" max="500" step="5" value={surface} onChange={(e) => setSurface(Number(e.target.value))} className="mt-4" data-testid="simulator-surface-slider" aria-label={`${t.simulator.surface} : ${surface} m²`} />
              <div className="flex justify-between font-mono text-[10px] tracking-[0.2em] uppercase text-limestone/40 mt-2">
                <span>20 m²</span><span>500 m²</span>
              </div>
            </div>

            <div className="mt-10">
              <label className="font-mono text-xs tracking-[0.2em] uppercase text-limestone/70 block mb-4">{t.simulator.projectType}</label>
              <div className="grid grid-cols-2 gap-2" data-testid="simulator-type-group">
                {Object.entries(t.simulator.types).map(([k, v]) => (
                  <button key={k} onClick={() => setProjectType(k)} data-testid={`simulator-type-${k}`}
                    className={`font-mono text-[11px] tracking-[0.1em] uppercase py-3 px-4 border transition-all text-left ${projectType === k ? "bg-terracotta border-terracotta text-limestone" : "border-limestone/30 text-limestone/80 hover:border-limestone"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-10">
              <label className="font-mono text-xs tracking-[0.2em] uppercase text-limestone/70 block mb-4">{t.simulator.finish}</label>
              <div className="grid grid-cols-3 gap-2" data-testid="simulator-finish-group">
                {Object.entries(t.simulator.finishes).map(([k, v]) => (
                  <button key={k} onClick={() => setFinish(k)} data-testid={`simulator-finish-${k}`}
                    className={`font-mono text-[11px] tracking-[0.1em] uppercase py-3 px-4 border transition-all ${finish === k ? "bg-terracotta border-terracotta text-limestone" : "border-limestone/30 text-limestone/80 hover:border-limestone"}`}>
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-limestone/20">
              <div className="flex items-baseline justify-between flex-wrap gap-2">
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-limestone/60">{t.simulator.estimate}</span>
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-limestone/40">{result ? "TTC" : t.simulator.locked}</span>
              </div>
              {result ? (
                <div className="font-mono text-2xl md:text-5xl text-terracotta mt-4 fade-in-up" data-testid="simulator-result">
                  {fmt(result.low_estimate_eur)} <span className="text-limestone/60 text-lg">—</span> {fmt(result.high_estimate_eur)} €
                </div>
              ) : (
                <div className="mt-4" data-testid="simulator-result-locked">
                  <div className="font-mono text-2xl md:text-5xl text-limestone/25 select-none tracking-wider">
                    ● ● ● &nbsp; <span className="text-limestone/15">—</span> &nbsp; ● ● ● &nbsp;€
                  </div>
                  <p className="font-sans text-xs md:text-sm text-limestone/55 mt-4 leading-relaxed max-w-md">
                    {t.simulator.lockedIntro}
                  </p>
                </div>
              )}
              <p className="font-sans text-xs text-limestone/50 mt-6 leading-relaxed">{t.simulator.note}</p>
              <button onClick={() => { setLeadOpen(true); setLeadStatus(null); }} className="btn-primary mt-8" style={{ background: "#B04A37", borderColor: "#B04A37" }} data-testid="simulator-cta">
                {result ? t.simulator.ctaAgain : t.simulator.cta} <ArrowRight size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lead capture dialog */}
      {leadOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/80 backdrop-blur-sm p-6" onClick={() => setLeadOpen(false)} data-testid="lead-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-limestone text-ink max-w-lg w-full border border-gridline p-8 md:p-10 relative">
            <button onClick={() => setLeadOpen(false)} aria-label="close" className="absolute top-4 right-4 font-mono text-xs text-ink/60 hover:text-terracotta" data-testid="lead-close">✕</button>
            <div className="section-label mb-4">{t.simulator.label}</div>
            <h3 className="font-serif text-3xl md:text-4xl text-ink font-light leading-tight">
              {t.simulator.lead.title} <span className="italic text-terracotta">{t.simulator.lead.titleItalic}</span>.
            </h3>
            <div className="mt-4 p-4 border border-gridline bg-stone/60">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-forest">{t.simulator.types[projectType]} · {surface} m² · {t.simulator.finishes[finish]}</div>
            </div>
            <p className="font-sans text-sm text-ink/75 mt-6 leading-relaxed">{t.simulator.lead.intro}</p>
            <form onSubmit={submitLead} className="mt-6 space-y-2" data-testid="lead-form">
              <input type="text" name="hp_homepage_url" value={leadForm.hp_homepage_url} onChange={(e) => setLeadForm({ ...leadForm, hp_homepage_url: e.target.value })} tabIndex="-1" autoComplete="nope" aria-hidden="true" data-form-type="other" data-lpignore="true" data-1p-ignore style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} />
              <div>
                <label htmlFor="lead-name" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.simulator.lead.name}</label>
                <input id="lead-name" required value={leadForm.name} onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })} className="input-paper" data-testid="lead-name" />
              </div>
              <div>
                <label htmlFor="lead-email" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.simulator.lead.email}</label>
                <input id="lead-email" required type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} className="input-paper" data-testid="lead-email" />
              </div>
              <div>
                <label htmlFor="lead-phone" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.simulator.lead.phone}</label>
                <input id="lead-phone" required type="tel" value={leadForm.phone} onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })} className="input-paper" data-testid="lead-phone" />
              </div>
              <div className="pt-4 flex items-center justify-between flex-wrap gap-3">
                <button type="submit" disabled={leadSubmitting || leadStatus === "success"} className="btn-primary disabled:opacity-40" data-testid="lead-submit">
                  {leadSubmitting ? "..." : (leadStatus === "success" ? "✓" : t.simulator.lead.submit)} <ArrowRight size={14} strokeWidth={1.5} />
                </button>
                {leadStatus === "success" && <span className="font-mono text-xs text-forest flex items-center gap-2" data-testid="lead-success"><Check size={14} /> {t.simulator.lead.success}</span>}
                {leadStatus === "error" && <span className="font-mono text-xs text-terracotta" data-testid="lead-error">{t.simulator.lead.error}</span>}
                {leadStatus === "rate_limit" && <span className="font-mono text-xs text-terracotta max-w-[260px] leading-relaxed" data-testid="lead-rate-limit">{t.simulator.lead.rateLimit}</span>}
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

/* ============================================================
   APPOINTMENT FORM
============================================================ */
const TIME_SLOTS = ["09:00", "10:30", "14:00", "15:30", "17:00"];

/* ============================================================
   CONTACT + APPOINTMENT — Section unifiée avec onglets
============================================================ */
const ContactAppointment = ({ t, lang }) => {
  const [tab, setTab] = useState("appointment"); // "appointment" | "message"

  /* ---- Appointment state ---- */
  const [apptForm, setApptForm] = useState({
    name: "", email: "", phone: "", date: "", time_slot: "",
    project_type: "renovation", location: "", notes: "", website: "",
  });
  const [dateObj, setDateObj] = useState(null);
  const [apptStatus, setApptStatus] = useState(null);
  const [apptSubmitting, setApptSubmitting] = useState(false);
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  }, []);

  const onApptChange = (k) => (e) => setApptForm({ ...apptForm, [k]: e.target.value });
  const selectSlot = (s) => setApptForm({ ...apptForm, time_slot: s });
  const selectDate = (d) => {
    if (!d) return;
    setDateObj(d);
    setApptForm({ ...apptForm, date: d.toISOString().slice(0, 10) });
    setDatePopoverOpen(false);
  };
  const submitAppt = async (e) => {
    e.preventDefault();
    setApptSubmitting(true);
    setApptStatus(null);
    try {
      await axios.post(`${API}/appointments`, apptForm);
      setApptStatus("success");
      setApptForm({ ...apptForm, name: "", email: "", phone: "", date: "", time_slot: "", location: "", notes: "" });
      setDateObj(null);
    } catch {
      setApptStatus("error");
    } finally {
      setApptSubmitting(false);
    }
  };
  const locale = lang === "fr" ? dfFr : dfEn;

  /* ---- Message (quick contact) state ---- */
  const [msgForm, setMsgForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
  const [msgStatus, setMsgStatus] = useState(null);
  const [msgSubmitting, setMsgSubmitting] = useState(false);
  const onMsgChange = (k) => (e) => setMsgForm({ ...msgForm, [k]: e.target.value });
  const submitMsg = async (e) => {
    e.preventDefault();
    setMsgSubmitting(true);
    setMsgStatus(null);
    try {
      await axios.post(`${API}/contact`, msgForm);
      setMsgStatus("success");
      setMsgForm({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
    } catch {
      setMsgStatus("error");
    } finally {
      setMsgSubmitting(false);
    }
  };

  const info = t._contact_info || {};

  return (
    <section id="contact" className="py-24 md:py-36 border-t border-gridline" data-testid="contact-section">
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Sidebar intro + coordonnées */}
          <div className="md:col-span-5">
            <div className="section-label mb-6">{t.contact.label}</div>
            <h2 className="font-serif text-5xl md:text-7xl leading-[0.95] text-ink font-light">
              {tab === "appointment" ? (
                <>{t.appointment.title} <span className="italic text-terracotta">{t.appointment.titleItalic}</span>{t.appointment.titleEnd}</>
              ) : (
                <>{t.contact.title} <span className="italic text-forest">{t.contact.titleItalic}</span>{t.contact.titleEnd}</>
              )}
            </h2>
            <p className="font-sans text-base text-ink/75 mt-8 max-w-md leading-relaxed">
              {tab === "appointment" ? t.appointment.intro : t.contact.intro}
            </p>
            <div className="mt-12 space-y-5 font-mono text-sm text-ink/80">
              <a href={`tel:${(info.phone || "05 64 72 13 18").replace(/\s/g, "")}`} className="flex items-start gap-3 hover:text-terracotta transition-colors" data-testid="contact-info-phone"><Phone size={16} strokeWidth={1.2} className="mt-0.5 text-forest" /> {info.phone || "05 64 72 13 18"}</a>
              <a href={`mailto:${info.email || "contact@cabinet-echo.com"}`} className="flex items-start gap-3 hover:text-terracotta transition-colors" data-testid="contact-info-email"><Mail size={16} strokeWidth={1.2} className="mt-0.5 text-forest" /> {info.email || "contact@cabinet-echo.com"}</a>
              <div className="flex items-start gap-3"><MapPin size={16} strokeWidth={1.2} className="mt-0.5 text-forest" /> {info.address || "Rochefort, 17300"}</div>
            </div>

            {/* Google Maps embed */}
            <div className="mt-10 border border-gridline overflow-hidden" data-testid="contact-map">
              <iframe
                title="Cabinet ECHO — 69 rue des pêcheurs d'Islande, Rochefort"
                src="https://maps.google.com/maps?q=Cabinet+ECHO+Rochefort&z=17&output=embed"
                width="100%"
                height="260"
                style={{ border: 0, display: "block" }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href="https://www.google.com/maps/search/?api=1&query=Cabinet+ECHO&query_place_id=ChIJ7QzvSf5AAUgRi0dFGpng-QQ"
                target="_blank"
                rel="noreferrer"
                className="block font-mono text-[10px] tracking-[0.2em] uppercase text-ink/60 hover:text-terracotta transition-colors px-3 py-2 border-t border-gridline bg-limestone"
                data-testid="contact-map-link"
              >
                {t.contact?.openInMaps || "Ouvrir dans Google Maps"} ↗
              </a>
            </div>
          </div>

          {/* Onglets + formulaires */}
          <div className="md:col-span-7">
            <div className="flex border-b border-gridline mb-0" data-testid="contact-tabs">
              <button
                type="button"
                onClick={() => setTab("appointment")}
                className={`font-mono text-[10px] tracking-[0.25em] uppercase px-6 py-4 border-b-2 -mb-[1px] transition-colors ${tab === "appointment" ? "border-terracotta text-ink" : "border-transparent text-ink/40 hover:text-ink"}`}
                data-testid="tab-appointment"
              >
                {t.appointment.tabLabel}
              </button>
              <button
                type="button"
                onClick={() => setTab("message")}
                className={`font-mono text-[10px] tracking-[0.25em] uppercase px-6 py-4 border-b-2 -mb-[1px] transition-colors ${tab === "message" ? "border-forest text-ink" : "border-transparent text-ink/40 hover:text-ink"}`}
                data-testid="tab-message"
              >
                {t.contact.tabLabel}
              </button>
            </div>

            {tab === "appointment" ? (
              <form onSubmit={submitAppt} className="border border-gridline border-t-0 p-8 md:p-12" data-testid="appointment-form">
                <input type="text" name="hp_homepage_url" value={apptForm.hp_homepage_url || ""} onChange={onApptChange("hp_homepage_url")} tabIndex="-1" autoComplete="nope" aria-hidden="true" data-form-type="other" data-lpignore="true" data-1p-ignore style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} data-testid="appt-honeypot" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  <div>
                    <label htmlFor="appt-name" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.appointment.name}</label>
                    <input id="appt-name" required value={apptForm.name} onChange={onApptChange("name")} className="input-paper" data-testid="appt-name" />
                  </div>
                  <div>
                    <label htmlFor="appt-email" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.appointment.email}</label>
                    <input id="appt-email" required type="email" value={apptForm.email} onChange={onApptChange("email")} className="input-paper" data-testid="appt-email" />
                  </div>
                  <div>
                    <label htmlFor="appt-phone" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.appointment.phone}</label>
                    <input id="appt-phone" required value={apptForm.phone} onChange={onApptChange("phone")} className="input-paper" data-testid="appt-phone" />
                  </div>
                  <div>
                    <label htmlFor="appt-location" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.appointment.location}</label>
                    <input id="appt-location" value={apptForm.location} onChange={onApptChange("location")} className="input-paper" data-testid="appt-location" placeholder="Rochefort..." />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 block">{t.appointment.date}</label>
                    <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                      <PopoverTrigger asChild>
                        <button type="button" className="input-paper flex items-center justify-between text-left cursor-pointer hover:border-b-terracotta" data-testid="appt-date-trigger">
                          <span className={dateObj ? "text-ink" : "text-ink/40 font-mono text-xs"}>
                            {dateObj ? format(dateObj, "d MMMM yyyy", { locale }) : "__ / __ / ____"}
                          </span>
                          <CalendarIcon size={14} strokeWidth={1.2} className="text-forest ml-2" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent align="start" className="p-0 bg-limestone border-gridline" data-testid="appt-date-calendar">
                        <Calendar mode="single" selected={dateObj} onSelect={selectDate} fromDate={minDate} locale={locale} initialFocus />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <label htmlFor="appt-project-type" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.appointment.project}</label>
                    <select id="appt-project-type" aria-label={t.appointment.project} value={apptForm.project_type} onChange={onApptChange("project_type")} className="input-paper" data-testid="appt-project-type">
                      {Object.entries(t.appointment.projects).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mt-8">
                  <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50 block mb-3">{t.appointment.time}</label>
                  <div className="flex flex-wrap gap-2" data-testid="appt-time-group">
                    {TIME_SLOTS.map((s) => (
                      <button key={s} type="button" onClick={() => selectSlot(s)} data-testid={`appt-time-${s}`}
                        className={`font-mono text-xs tracking-[0.1em] py-2 px-4 border transition-all ${apptForm.time_slot === s ? "bg-ink text-limestone border-ink" : "border-gridline text-ink hover:border-ink"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mt-8">
                  <label htmlFor="appt-notes" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.appointment.notes}</label>
                  <textarea id="appt-notes" rows="3" value={apptForm.notes} onChange={onApptChange("notes")} className="input-paper resize-none" data-testid="appt-notes" />
                </div>
                <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                  <button type="submit" disabled={apptSubmitting || !apptForm.time_slot || !apptForm.date} className="btn-primary disabled:opacity-40" data-testid="appt-submit">
                    {apptSubmitting ? "..." : t.appointment.submit} <ArrowRight size={14} strokeWidth={1.5} />
                  </button>
                  {apptStatus === "success" && <p className="font-mono text-xs text-forest flex items-center gap-2" data-testid="appt-success"><Check size={14} /> {t.appointment.success}</p>}
                  {apptStatus === "error" && <p className="font-mono text-xs text-terracotta" data-testid="appt-error">{t.appointment.error}</p>}
                </div>
              </form>
            ) : (
              <form onSubmit={submitMsg} className="border border-gridline border-t-0 p-8 md:p-12" data-testid="contact-form">
                <input type="text" name="hp_homepage_url" value={msgForm.hp_homepage_url || ""} onChange={onMsgChange("hp_homepage_url")} tabIndex="-1" autoComplete="nope" aria-hidden="true" data-form-type="other" data-lpignore="true" data-1p-ignore style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, width: 0 }} data-testid="contact-honeypot" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                  <div>
                    <label htmlFor="contact-name" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.contact.name}</label>
                    <input id="contact-name" required value={msgForm.name} onChange={onMsgChange("name")} className="input-paper" data-testid="contact-name" />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.contact.email}</label>
                    <input id="contact-email" required type="email" value={msgForm.email} onChange={onMsgChange("email")} className="input-paper" data-testid="contact-email" />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.contact.phone}</label>
                    <input id="contact-phone" required type="tel" value={msgForm.phone} onChange={onMsgChange("phone")} className="input-paper" data-testid="contact-phone" />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.contact.subject}</label>
                    <input id="contact-subject" value={msgForm.subject} onChange={onMsgChange("subject")} className="input-paper" data-testid="contact-subject" />
                  </div>
                  <div className="md:col-span-2">
                    <label htmlFor="contact-message" className="font-mono text-[10px] tracking-[0.2em] uppercase text-ink/50">{t.contact.message}</label>
                    <textarea id="contact-message" required rows="5" value={msgForm.message} onChange={onMsgChange("message")} className="input-paper resize-none" data-testid="contact-message" />
                  </div>
                </div>
                <div className="mt-10 flex items-center justify-between flex-wrap gap-4">
                  <button type="submit" disabled={msgSubmitting} className="btn-primary disabled:opacity-40" data-testid="contact-submit">
                    {msgSubmitting ? "..." : t.contact.submit} <ArrowRight size={14} strokeWidth={1.5} />
                  </button>
                  {msgStatus === "success" && <p className="font-mono text-xs text-forest flex items-center gap-2" data-testid="contact-success"><Check size={14} /> {t.contact.success}</p>}
                  {msgStatus === "error" && <p className="font-mono text-xs text-terracotta" data-testid="contact-error">{t.contact.error}</p>}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   FOOTER
============================================================ */
const Footer = ({ t }) => {
  const { content } = useSiteContent();
  const cmsCities = Array.isArray(content?.cities) ? content.cities : null;
  const footerCities = cmsCities && cmsCities.length ? cmsCities : CITIES;
  return (
  <footer className="bg-forest text-limestone py-20 relative overflow-hidden">
    <div className="absolute inset-0 opacity-5 blueprint-grid pointer-events-none" />
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 relative">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-16 border-b border-limestone/15">
        <div className="md:col-span-5">
          <div className="flex items-center gap-4">
          <img loading="lazy" decoding="async" src={LOGO_URL} alt="Cabinet ECHO — logo" width="80" height="80" className="w-20 h-20 object-contain" />
            <div className="flex flex-col leading-none">
              <span className="font-mono text-[11px] tracking-[0.38em] uppercase text-terracotta">Cabinet</span>
              <span className="font-serif text-6xl font-light tracking-[-0.02em] -mt-0.5 leading-none">ECHO</span>
            </div>
          </div>
          <p className="font-mono text-xs tracking-[0.15em] uppercase text-limestone/60 mt-6">{t.footer.tag}</p>
          <p className="font-serif italic text-2xl text-limestone/85 mt-10 max-w-md leading-snug">
            « {t.hero.title1} {t.hero.title2} {t.hero.title3} »
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-limestone/50 mb-4">{t.footer.contactTitle}</p>
          <ul className="space-y-3 font-sans text-sm text-limestone/85">
            <li>contact@cabinet-echo.com</li>
            <li>05 64 72 13 18</li>
            <li>{t.footer.address}</li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-limestone/50 mb-4">{t.footer.followTitle}</p>
          <div className="flex gap-3">
            <a href="https://www.facebook.com/cabinetECHO/" target="_blank" rel="noreferrer" className="border border-limestone/25 p-3 hover:bg-terracotta hover:border-terracotta transition-colors" data-testid="social-facebook" aria-label="Facebook">
              <Facebook size={14} strokeWidth={1.5} />
            </a>
            <a href="https://www.instagram.com/cabinet.echo/" target="_blank" rel="noreferrer" className="border border-limestone/25 p-3 hover:bg-terracotta hover:border-terracotta transition-colors" data-testid="social-instagram" aria-label="Instagram">
              <Instagram size={14} strokeWidth={1.5} />
            </a>
            <a href="https://www.google.com/maps/place/Cabinet+ECHO+Ma%C3%AEtrise+d'oeuvre+Rochefort/@45.9466417,-0.9884385,883m/data=!3m2!1e3!4b1!4m6!3m5!1s0x480140fe49ef0ced:0x4f9e0991a45478b!8m2!3d45.946638!4d-0.9858636!16s%2Fg%2F11csb1qh5g?entry=ttu&g_ep=EgoyMDI2MDQyMi4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noreferrer" className="border border-limestone/25 p-3 hover:bg-terracotta hover:border-terracotta transition-colors inline-flex items-center justify-center font-mono text-[10px] tracking-[0.15em] font-semibold w-[38px] h-[38px]" data-testid="social-google" aria-label="Google Business Profile · Voir nos avis">
              G
            </a>
          </div>
        </div>

        <div className="md:col-span-2">
          <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-limestone/50 mb-4">{t.footer.navTitle}</p>
          <ul className="space-y-2 font-sans text-sm text-limestone/85">
            <li><Link to="/equipe" className="hover:text-terracotta transition-colors" data-testid="footer-team">{t.footer.team}</Link></li>
            <li><Link to="/methode" className="hover:text-terracotta transition-colors" data-testid="footer-process">{t.footer.method}</Link></li>
            <li><Link to="/lexique" className="hover:text-terracotta transition-colors" data-testid="footer-glossary">{t.footer.glossary}</Link></li>
            <li><Link to="/#projects" className="hover:text-terracotta transition-colors" data-testid="footer-projects">{t.nav.projects}</Link></li>
            <li><Link to="/#simulator" className="hover:text-terracotta transition-colors" data-testid="footer-simulator">{t.nav.simulator}</Link></li>
            <li><Link to="/blog" className="hover:text-terracotta transition-colors" data-testid="footer-blog">{t.nav.blog}</Link></li>
          </ul>
        </div>
      </div>

      {/* Zones d'intervention — internal links to localized city pages (SEO + editorial discovery) */}
      <div className="mt-12 pt-8 border-t border-limestone/10" data-testid="footer-zones">
        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-limestone/50 mb-4">{t.footer.zonesTitle}</p>
        <div className="flex flex-wrap gap-x-8 gap-y-3 font-serif italic text-base md:text-lg">
          {footerCities.map((c) => (
            <Link
              key={c.slug}
              to={`/maitrise-oeuvre/${c.slug}`}
              className="text-limestone/85 hover:text-terracotta transition-colors group inline-flex items-baseline gap-2"
              data-testid={`footer-zone-${c.slug}`}
            >
              {c.name} <span className="not-italic font-mono text-[10px] tracking-[0.2em] text-terracotta opacity-0 group-hover:opacity-100 transition-opacity">→</span>
            </Link>
          ))}
        </div>
      </div>

      <NetworkBadges title={t.footer.networksTitle} />

      <div className="mt-8 flex flex-wrap items-center justify-between gap-4 font-mono text-[10px] tracking-[0.2em] uppercase text-limestone/50">
        <span>© {new Date().getFullYear()} · Cabinet ECHO · {t.footer.rights}</span>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <Link to="/mentions-legales" className="hover:text-terracotta transition-colors" data-testid="footer-legal">{t.footer.legal}</Link>
          <Link to="/politique-de-confidentialite" className="hover:text-terracotta transition-colors" data-testid="footer-privacy">{t.footer.privacy}</Link>
          <span className="flex items-center gap-2"><Sparkles size={12} strokeWidth={1.2} /> {t.footer.craft}</span>
        </div>
      </div>
    </div>
  </footer>
  );
};

/* ============================================================
   HOMEPAGE
============================================================ */
const FAQ = ({ t }) => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 md:py-36 border-t border-gridline" data-testid="faq-section">
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <div className="section-label mb-6">{t.faq.label}</div>
            <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] text-ink font-light">
              {t.faq.title} <span className="italic text-forest">{t.faq.titleItalic}</span>
            </h2>
          </div>
          <p className="md:col-span-6 md:col-start-7 font-sans text-lg text-ink/75 leading-relaxed">
            {t.faq.intro}
          </p>
        </div>
        <div className="border-t border-gridline">
          {t.faq.items.map((q, i) => (
            <div key={i} className="border-b border-gridline" data-testid={`faq-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full py-7 flex items-start justify-between gap-6 text-left group"
                data-testid={`faq-toggle-${i}`}
              >
                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-forest mt-2 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <span className="flex-1 font-serif text-xl md:text-2xl text-ink group-hover:text-terracotta transition-colors">
                  {q.q}
                </span>
                <span className={`font-serif italic text-3xl text-ink/40 group-hover:text-terracotta transition-all duration-300 ${open === i ? "rotate-45" : ""}`}>+</span>
              </button>
              <div
                className={`overflow-hidden transition-all duration-500 ease-out ${open === i ? "max-h-96 pb-7" : "max-h-0"}`}
                data-testid={`faq-answer-${i}`}
              >
                <p className="font-sans text-base md:text-lg text-ink/75 leading-relaxed pl-16 pr-12 max-w-3xl">{q.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const HomePage = ({ t, lang, heroImage }) => {
  useSeo({
    title: lang === "fr"
      ? "Cabinet ECHO — Maître d'œuvre à Rochefort · Construction · Rénovation · Extension"
      : "Cabinet ECHO — Project Management in Rochefort · Construction · Renovation · Extension",
    description: lang === "fr"
      ? "Cabinet ECHO pilote vos projets de construction, rénovation et extension en Charente-Maritime. Étude, planification, coordination d'artisans, livraison clés en main."
      : "Cabinet ECHO leads your construction, renovation and extension projects in Charente-Maritime, France.",
    canonical: "https://cabinet-echo.com/",
    alternates: {
      fr: "https://cabinet-echo.com/",
      en: "https://cabinet-echo.com/?lang=en",
    },
  });

  return (
    <>
      <Hero t={t} heroImage={heroImage} />
      <Marquee items={t.marquee} />
      <About t={t} />
      <Services t={t} />
      <Projects t={t} />
      <Testimonials t={t} />
      <Simulator t={t} />
      <FAQ t={t} />
      <ContactAppointment t={t} lang={lang} />
    </>
  );
};

/* ============================================================
   Site shell — nav + footer visible on public routes
============================================================ */
/* ============================================================
   Floating mobile "call" button — bottom-right
============================================================ */
const MobileCallButton = ({ t }) => {
  const phone = (t._contact_info?.phone || "05 64 72 13 18").replace(/\s/g, "");
  return (
    <a
      href={`tel:${phone}`}
      className="md:hidden fixed bottom-5 left-5 z-40 flex items-center gap-2 bg-terracotta text-limestone px-5 py-3.5 rounded-full shadow-lg shadow-terracotta/30 active:scale-95 transition-transform font-mono text-[11px] tracking-[0.2em] uppercase"
      aria-label={t.nav?.callNow || "Appeler maintenant"}
      data-testid="mobile-call-btn"
    >
      <Phone size={16} strokeWidth={2} />
      <span>{t.nav?.callNow || "Appeler"}</span>
    </a>
  );
};

/* ============================================================
   Site shell — nav + footer visible on public routes
============================================================ */
const PublicShell = ({ lang, setLang, t, children }) => (
  <>
    <Nav lang={lang} setLang={setLang} t={t} />
    <main id="main-content">{children}</main>
    <Footer t={t} />
    <MobileCallButton t={t} />
  </>
);

/* ============================================================
   APP
============================================================ */
const AppRouter = () => {
  // Initial lang: URL param `?lang=en` wins, then defaults to French.
  const getInitialLang = () => {
    if (typeof window === "undefined") return "fr";
    const p = new URLSearchParams(window.location.search).get("lang");
    return p === "en" ? "en" : "fr";
  };
  const [lang, setLang] = useState(getInitialLang);
  const { content } = useSiteContent();
  const baseT = translations[lang];
  const t = useMemo(() => mergeTranslationsWithContent(baseT, content, lang), [baseT, content, lang]);
  const heroImage = resolveHeroImage(content, HERO_IMAGE);

  const location = useLocation();

  useEffect(() => {
    document.documentElement.lang = lang;
    // Sync URL ?lang param so hreflang / shareable EN URLs stay correct.
    try {
      const url = new URL(window.location.href);
      if (lang === "en") url.searchParams.set("lang", "en");
      else url.searchParams.delete("lang");
      window.history.replaceState({}, "", url.toString());
    } catch { /* no-op */ }
  }, [lang]);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
    <ScrollManager />
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path="/" element={<PublicShell lang={lang} setLang={setLang} t={t}><HomePage t={t} lang={lang} heroImage={heroImage} /></PublicShell>} />
      <Route path="/projets/:slug" element={<PublicShell lang={lang} setLang={setLang} t={t}><ProjectDetail lang={lang} content={content} /></PublicShell>} />
      <Route path="/blog" element={<PublicShell lang={lang} setLang={setLang} t={t}><Blog lang={lang} /></PublicShell>} />
      <Route path="/blog/:slug" element={<PublicShell lang={lang} setLang={setLang} t={t}><BlogArticle lang={lang} /></PublicShell>} />
      <Route path="/equipe" element={<PublicShell lang={lang} setLang={setLang} t={t}><Team lang={lang} content={content} /></PublicShell>} />
      <Route path="/lexique" element={<PublicShell lang={lang} setLang={setLang} t={t}><Glossary lang={lang} content={content} /></PublicShell>} />
      <Route path="/methode" element={<PublicShell lang={lang} setLang={setLang} t={t}><Process lang={lang} content={content} /></PublicShell>} />
      <Route path="/maitrise-oeuvre/:citySlug" element={<PublicShell lang={lang} setLang={setLang} t={t}><City /></PublicShell>} />
      <Route path="/mentions-legales" element={<PublicShell lang={lang} setLang={setLang} t={t}><LegalMentions lang={lang} /></PublicShell>} />
      <Route path="/politique-de-confidentialite" element={<PublicShell lang={lang} setLang={setLang} t={t}><PrivacyPolicy lang={lang} /></PublicShell>} />
      <Route path="/login" element={<PublicShell lang={lang} setLang={setLang} t={t}><Login /></PublicShell>} />

      <Route path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <PublicShell lang={lang} setLang={setLang} t={t}><AdminShell /></PublicShell>
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="contacts" element={<AdminContacts />} />
        <Route path="appointments" element={<AdminAppointments />} />
        <Route path="blog" element={<AdminBlog />} />
        <Route path="content" element={<AdminContent />} />
      </Route>
    </Routes>
    </Suspense>
    <ExitIntent lang={lang} />
    </>
  );
};

function App() {
  return (
    <div className="App" data-testid="site-root">
      <BrowserRouter>
        <AuthProvider>
          <SiteContentProvider>
            <AppRouter />
          </SiteContentProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
